import pluralize from 'pluralize'

import type { Table } from '@/components/ui/table/types'
import {
  ProcessButton,
  ProjectStateBar,
  type Statement,
  TrialProcessingNote,
  useProject,
  useStatements,
  useStatementTally,
} from '@/modules/projects'
import {
  hasProjectFailed,
  isProjectProcessing,
  isStatementToStart,
} from '@/modules/projects/status'

interface ProcessPanelProps {
  table: Table<Statement> | null
}

const DOT_DELAY_SECONDS = 0.16

const AnimatedEllipsis = () => (
  <span aria-hidden>
    {[0, 1, 2].map((position) => (
      <span
        key={position}
        className="inline-block animate-typing-dot motion-reduce:animate-none"
        style={{ animationDelay: `${position * DOT_DELAY_SECONDS}s` }}
      >
        .
      </span>
    ))}
  </span>
)

const panelHeading = (isProcessing: boolean, isEmpty: boolean) => {
  if (isProcessing) {
    return {
      title: (
        <>
          Processing
          <AnimatedEllipsis />
        </>
      ),
      description: null,
    }
  }
  return {
    title: 'Ready to process',
    description: isEmpty
      ? 'Upload PDFs to enable processing.'
      : 'Extraction starts on every uploaded statement.',
  }
}

export const ProcessPanel = ({ table }: ProcessPanelProps) => {
  const project = useProject()
  const statements = useStatements()
  const { statementCount, totalPages } = useStatementTally()

  const isEmpty = statementCount === 0
  const isProcessing = isProjectProcessing(project)
  const heading = panelHeading(isProcessing, isEmpty)
  // While a run is going, the button only matters for files added after it started.
  const showProcessButton = !isProcessing || statements.some(isStatementToStart)

  const label = hasProjectFailed(project)
    ? 'Retry all failed statements'
    : isEmpty
      ? 'Process statements'
      : `Process ${statementCount} ${pluralize('statement', statementCount)}`

  return (
    <div className="@4xl:sticky @4xl:top-6 @4xl:max-h-[calc(100vh-3rem)] @4xl:overflow-y-auto">
      <div className="flex items-center justify-between gap-4 border bg-white px-5 py-3 shadow-lg @4xl:flex-col @4xl:items-stretch rounded-lg @4xl:p-5 @4xl:shadow-none">
        <div className="min-w-0">
          <h3 className="hidden font-semibold leading-6 tracking-tight @4xl:block">
            {heading.title}
          </h3>
          {heading.description && (
            <p className="hidden text-sm text-muted-foreground @4xl:mt-1 @4xl:block">
              {heading.description}
            </p>
          )}

          {!isEmpty && (
            <output className="block truncate text-sm tabular-nums text-muted-foreground @4xl:mt-4">
              <span className="font-semibold text-foreground">
                {statementCount} {pluralize('statement', statementCount)}
              </span>{' '}
              · {totalPages} {pluralize('page', totalPages)}
            </output>
          )}
        </div>

        {isProcessing && (
          <ProjectStateBar
            compact
            alwaysVisible
            className="hidden @4xl:block"
          />
        )}
        <TrialProcessingNote className="hidden @4xl:flex" />

        {showProcessButton && (
          <ProcessButton
            table={table}
            className="@4xl:w-full"
            messagePlacement="block"
          >
            {label}
          </ProcessButton>
        )}
      </div>
    </div>
  )
}
